import React from 'react';
import { Box, type IconButtonProps, type SxProps, type Theme } from '@mui/material';
import {
    checkIcon,
    chevronLeftIcon,
    chevronRightIcon,
    deleteIcon,
    downloadIcon,
    downloadSignedIcon,
    editIcon,
    moveIcon,
    preview as viewIcon,
    signatureIcon,
    uploadIcon,
    refreshDataIcon,
} from '@europrocurement/l2d-icons';
import { FlexyIconButton } from '../../atoms/FlexyIconButton/FlexyIconButton';
import { useL2DTheme } from '../../../hooks/useL2DTheme';

export type ItemActionsProps = {
    onEdit?: IconButtonProps['onClick'];
    onDelete?: IconButtonProps['onClick'];
    onShow?: IconButtonProps['onClick'];
    onDownload?: IconButtonProps['onClick'];
    onDownloadSigned?: IconButtonProps['onClick'];
    onUpload?: IconButtonProps['onClick'];
    onSign?: IconButtonProps['onClick'];
    onMove?: IconButtonProps['onClick'];
    onValidate?: IconButtonProps['onClick'];
    onExpand?: IconButtonProps['onClick'];
    onRefresh?: IconButtonProps['onClick'];
    editTooltip?: string;
    deleteTooltip?: string;
    showTooltip?: string;
    downloadTooltip?: string;
    downloadSignedTooltip?: string;
    uploadTooltip?: string;
    signTooltip?: string;
    moveTooltip?: string;
    validateTooltip?: string;
    expandTooltip?: string;
    refreshTooltip?: string;
    /** Controls the icon shown for onExpand: true = chevronLeft (collapse), false = chevronRight (expand) */
    expanded?: boolean;
    /** Button size in px (default: 24) */
    size?: number;
    /** Icon font-size (default: '11px') */
    iconSize?: string;
    /**
     * Controls the slide animation.
     * When provided, the group fades/slides in (visible=true) or out (visible=false).
     * When omitted, the group is always rendered without animation.
     */
    visible?: boolean;
    /**
     * Side the group slides in from when becoming visible (default: 'left').
     * 'left'  → starts at translateX(-8px), slides rightward.
     * 'right' → starts at translateX(+8px), slides leftward.
     */
    slideDirection?: 'left' | 'right';
    orientation?: 'row' | 'column';
    disabled?: IconButtonProps['disabled'];
    confirmation?: boolean;
    sx?: SxProps<Theme>;
};

export const ItemActions: React.FunctionComponent<ItemActionsProps> = function ({
    onEdit,
    onDelete,
    onShow,
    onDownload,
    onDownloadSigned,
    onUpload,
    onSign,
    onMove,
    onValidate,
    onExpand,
    onRefresh,
    editTooltip,
    deleteTooltip,
    showTooltip,
    downloadTooltip,
    downloadSignedTooltip,
    uploadTooltip,
    signTooltip,
    moveTooltip,
    validateTooltip,
    expandTooltip,
    refreshTooltip,
    expanded = false,
    size = 24,
    iconSize = '11px',
    visible,
    slideDirection = 'left',
    orientation = 'row',
    disabled = false,
    confirmation = false,
    sx,
}) {
    const { palette } = useL2DTheme();
    const editToken = palette.crudAction?.edit;
    const deleteToken = palette.crudAction?.delete;
    const confirmationToken = palette.crudAction?.confirmation;
    const viewToken = palette.crudAction?.view;
    const downloadToken = palette.crudAction?.download;
    const downloadSignedToken = palette.crudAction?.downloadSigned;
    const uploadToken = palette.crudAction?.upload;
    const signToken = palette.crudAction?.sign;
    const moveToken = palette.crudAction?.move;
    const validateToken = palette.crudAction?.validate;

    // Design intent: icon is neutral at rest (blends with context), becomes
    // semantically colored on hover. The token provides the hover colors;
    // the neutral rest color is a local layout decision.
    const buttonSx = () => ({
        width: size,
        height: size,
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease-in-out',
    });

    const hasAnimation = visible !== undefined;
    const slideOffset = slideDirection === 'right' ? '8px' : '-8px';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: orientation,
                alignItems: 'center',
                gap: '4px',
                ...sx,
                ...(hasAnimation && {
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateX(0)' : `translateX(${slideOffset})`,
                    transition:
                        'opacity 0.25s ease, transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    pointerEvents: visible ? 'auto' : 'none',
                }),
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {onExpand && (
                <FlexyIconButton
                    icon={expanded ? chevronLeftIcon : chevronRightIcon}
                    tooltipOverwriteProps={expandTooltip ? { title: expandTooltip } : undefined}
                    onClick={onExpand}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onValidate && (
                <FlexyIconButton
                    icon={checkIcon}
                    tooltipOverwriteProps={validateTooltip ? { title: validateTooltip } : undefined}
                    normalHoverColor={validateToken?.textColor}
                    onClick={onValidate}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onShow && (
                <FlexyIconButton
                    icon={viewIcon}
                    tooltipOverwriteProps={showTooltip ? { title: showTooltip } : undefined}
                    normalHoverColor={viewToken?.textColor}
                    onClick={onShow}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onSign && (
                <FlexyIconButton
                    icon={signatureIcon}
                    tooltipOverwriteProps={signTooltip ? { title: signTooltip } : undefined}
                    normalHoverColor={signToken?.textColor}
                    onClick={onSign}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onDownloadSigned && (
                <FlexyIconButton
                    icon={downloadSignedIcon}
                    tooltipOverwriteProps={
                        downloadSignedTooltip ? { title: downloadSignedTooltip } : undefined
                    }
                    normalHoverColor={downloadSignedToken?.textColor}
                    onClick={onDownloadSigned}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onDownload && (
                <FlexyIconButton
                    icon={downloadIcon}
                    tooltipOverwriteProps={downloadTooltip ? { title: downloadTooltip } : undefined}
                    normalHoverColor={downloadToken?.textColor}
                    onClick={onDownload}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onUpload && (
                <FlexyIconButton
                    icon={uploadIcon}
                    tooltipOverwriteProps={uploadTooltip ? { title: uploadTooltip } : undefined}
                    normalHoverColor={uploadToken?.textColor}
                    onClick={onUpload}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onEdit && (
                <FlexyIconButton
                    icon={editIcon}
                    tooltipOverwriteProps={editTooltip ? { title: editTooltip } : undefined}
                    normalHoverColor={editToken?.textColor}
                    onClick={onEdit}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                />
            )}
            {onMove && (
                <FlexyIconButton
                    icon={moveIcon}
                    tooltipOverwriteProps={moveTooltip ? { title: moveTooltip } : undefined}
                    normalHoverColor={moveToken?.textColor}
                    onClick={onMove}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
            {onDelete && (
                <FlexyIconButton
                    icon={deleteIcon}
                    tooltipOverwriteProps={deleteTooltip ? { title: deleteTooltip } : undefined}
                    normalHoverColor={deleteToken?.textColor}
                    confirmationHoverColor={confirmationToken?.textColor}
                    onClick={onDelete}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    confirmation={confirmation}
                    confirmationTooltip="Cliquez pour confirmer la suppression"
                    disabled={disabled}
                />
            )}
            {onRefresh && (
                <FlexyIconButton
                    icon={refreshDataIcon}
                    tooltipOverwriteProps={refreshTooltip ? { title: refreshTooltip } : undefined}
                    onClick={onRefresh}
                    buttonOverwriteProps={{ sx: buttonSx() }}
                    iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                    disabled={disabled}
                />
            )}
        </Box>
    );
};
